import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Tabs,
  Tab,
  Avatar,
  Box,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Stack,
} from "@mui/material";
import { certificateApi } from "../certificateApi";
import { idCardApi } from "./idCardApi";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { masterDataService, ClassRecord, SectionRecord, SessionRecord } from "../../../../services/master-data.service";

interface Props {
  form: {
    mode: "AUTO" | "MANUAL";
    studentId?: string;
    studentIds?: string[];
    photoUrl?: string;
    studentName?: string;
    scholarNo?: string;
    className?: string;
    sectionName?: string;
    dob?: string;
    bloodGroup?: string;
    validUpto?: string;
  };
  onChange: Dispatch<
    SetStateAction<{
      mode: "AUTO" | "MANUAL";
      studentId?: string;
      studentIds?: string[];
      photoUrl?: string;
      studentName?: string;
      scholarNo?: string;
      className?: string;
      sectionName?: string;
      dob?: string;
      bloodGroup?: string;
      validUpto?: string;
    }>
  >;
  onSubmit: () => void;
  onGenerateFilteredPdf: (filters: {
    sessionId?: string;
    classId?: string;
    sectionId?: string;
    search?: string;
  }) => void;
  onGenerateFilteredA4Pdf: (filters: {
    sessionId?: string;
    classId?: string;
    sectionId?: string;
    search?: string;
  }) => void;
  onGenerateCorrectionCheckPdf: (filters: {
    sessionId?: string;
    classId?: string;
    sectionId?: string;
    search?: string;
  }) => void;
  onBulkManualUpload: (file: File) => void;
}

const StudentIdCardForm: React.FC<Props> = ({
  form,
  onChange,
  onSubmit,
  onGenerateFilteredPdf,
  onGenerateFilteredA4Pdf,
  onGenerateCorrectionCheckPdf,
  onBulkManualUpload,
}) => {
  const [students, setStudents] = useState<any[]>([]);
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [classes, setClasses] = useState<ClassRecord[]>([]);
  const [sections, setSections] = useState<SectionRecord[]>([]);
  const [filters, setFilters] = useState<{
    sessionId?: string;
    classId?: string;
    sectionId?: string;
    search?: string;
  }>({});

  const loadStudents = async (params?: {
    sessionId?: string;
    classId?: string;
    sectionId?: string;
    search?: string;
  }) => {
    try {
      const res = await certificateApi.students(params || {});
      setStudents(res);
      const selected = new Set(form.studentIds || []);
      const validSelected = res.map((s) => s.id).filter((id) => selected.has(id));
      onChange((p) => ({
        ...p,
        studentIds: validSelected,
        studentId: validSelected[0],
      }));
    } catch (e) {
      console.log("IDCard: students error", e);
    }
  };

  useEffect(() => {
    Promise.all([masterDataService.getAllSessions(), masterDataService.getClasses()])
      .then(([sessionRows, classRows]) => {
        setSessions(Array.isArray(sessionRows) ? sessionRows : []);
        setClasses(Array.isArray(classRows) ? classRows : []);
      })
      .catch((e) => {
        console.log("IDCard: failed to load masters", e);
      });
    loadStudents({});
  }, []);

  useEffect(() => {
    if (!filters.classId) {
      setSections([]);
      setFilters((p) => ({ ...p, sectionId: undefined }));
      return;
    }
    masterDataService
      .getSections(filters.classId)
      .then((res) => setSections(Array.isArray(res) ? res : []))
      .catch(() => setSections([]));
  }, [filters.classId]);

  const f = (name: string, value: any) => onChange((p) => ({ ...p, [name]: value }));

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await idCardApi.upload(file);
      f("photoUrl", res.url);
    } catch (e) {
      console.error("Upload failed", e);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: "AUTO" | "MANUAL") => {
    f("mode", newValue);
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={form.mode} onChange={handleTabChange}>
          <Tab label="Fetch From Student" value="AUTO" />
          <Tab label="Manual Entry" value="MANUAL" />
        </Tabs>
      </Box>

      <Grid container spacing={2} alignItems="center">
        {form.mode === "AUTO" && (
          <>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Session"
                value={filters.sessionId || ""}
                onChange={(e) => setFilters((p) => ({ ...p, sessionId: e.target.value || undefined }))}
              >
                <MenuItem value="">All Sessions</MenuItem>
                {sessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Class"
                value={filters.classId || ""}
                onChange={(e) =>
                  setFilters((p) => ({
                    ...p,
                    classId: e.target.value || undefined,
                    sectionId: undefined,
                  }))
                }
              >
                <MenuItem value="">All Classes</MenuItem>
                {classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Section"
                value={filters.sectionId || ""}
                onChange={(e) => setFilters((p) => ({ ...p, sectionId: e.target.value || undefined }))}
                disabled={!filters.classId}
              >
                <MenuItem value="">All Sections</MenuItem>
                {sections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                value={filters.search || ""}
                onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value || undefined }))}
                placeholder="Name / Admission / Scholar"
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => loadStudents(filters)}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="contained"
                  onClick={() => onGenerateFilteredPdf(filters)}
                >
                  Generate & Download All Filtered
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => onGenerateFilteredA4Pdf(filters)}
                >
                  Generate A4 Bulk Sheet
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => onGenerateCorrectionCheckPdf(filters)}
                >
                  Correction Check PDF
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const all = students.map((s) => s.id);
                    onChange((p) => ({ ...p, studentIds: all, studentId: all[0] }));
                  }}
                  disabled={students.length === 0}
                >
                  Select All Filtered ({students.length})
                </Button>
                <Button
                  variant="text"
                  onClick={() => onChange((p) => ({ ...p, studentIds: [], studentId: undefined }))}
                  disabled={!form.studentIds?.length}
                >
                  Clear Selection
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="bulk-student-select-label">Select Students</InputLabel>
                <Select
                  labelId="bulk-student-select-label"
                  multiple
                  value={form.studentIds || []}
                  onChange={(e) => {
                    const ids = e.target.value as string[];
                    onChange((p) => ({ ...p, studentIds: ids, studentId: ids[0] }));
                    const first = students.find((x) => x.id === ids[0]);
                    if (first?.studentPhotoUrl) f("photoUrl", first.studentPhotoUrl);
                  }}
                  input={<OutlinedInput label="Select Students" />}
                  renderValue={(selected) => `${selected.length} student(s) selected`}
                >
                  {students.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={(form.studentIds || []).indexOf(s.id) > -1} />
                      <ListItemText
                        primary={`${s.admissionNumber} - ${s.firstName} ${s.lastName}`}
                        secondary={`${s.class?.name || ""} ${s.section?.name || ""}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {form.mode === "MANUAL" && (
          <>
            <Grid item xs={12} md={2} display="flex" justifyContent="center">
              <Avatar src={form.photoUrl} sx={{ width: 80, height: 80, border: "1px solid #ddd" }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                Upload Photo
                <input type="file" hidden accept="image/*" onChange={onFile} />
              </Button>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Name" value={form.studentName || ""} onChange={(e) => f("studentName", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label="Scholar No" value={form.scholarNo || ""} onChange={(e) => f("scholarNo", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label="Class" value={form.className || ""} onChange={(e) => f("className", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={1}>
              <TextField fullWidth label="Section" value={form.sectionName || ""} onChange={(e) => f("sectionName", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="date" label="DOB" value={form.dob || ""} onChange={(e) => f("dob", e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label="Blood Group" value={form.bloodGroup || ""} onChange={(e) => f("bloodGroup", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="date" label="Valid Upto" value={form.validUpto || ""} onChange={(e) => f("validUpto", e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={2}>
               {/* Spacer or additional field */}
            </Grid>
          </>
        )}

        {form.mode === "MANUAL" && (
          <Grid item xs={12}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
            >
              Bulk Upload Manual Entries (Excel/PDF)
              <input
                hidden
                type="file"
                accept=".xlsx,.xls,.pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onBulkManualUpload(file);
                }}
              />
            </Button>
          </Grid>
        )}

        <Grid item xs={12} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={onSubmit}
            size="large"
            disabled={form.mode === "AUTO" && !(form.studentIds && form.studentIds.length > 0)}
          >
            {form.mode === "AUTO"
              ? `Generate ${(form.studentIds || []).length || 0} ID Card(s)`
              : "Save ID Card"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudentIdCardForm;
